
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { PanZoomModel } from 'ngx-panzoom';
import { Observable } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { getTrustedXbrliPolicy } from 'src/app/_helpers/trusted-types-policy';
import { renderDocumentIntoShadowRoot } from 'src/app/_helpers/shadow-document-renderer';
import { DocumentTypeEnum } from 'src/app/constants/document-type.enum';
import { StatusRequestModel } from 'src/app/models/status/status-request.model';
import { DocumentApiClient } from 'src/app/services/document.apiclient';


export const ViewXbrlOrTxtModalOptions: NgbModalOptions = {
  backdrop: 'static',
  keyboard: false,
  centered: true,
  size: 'xl',
  windowClass: 'renderedModal'
};

@Component({
    templateUrl: './view-xbrl-or-txt.modal.html',
    styleUrl: './view-xbrl-or-txt.modal.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ViewXbrlOrTxtModal implements OnInit {
  public documentTypeEnum = DocumentTypeEnum;

  public get setBackgroundColorWhite(): boolean {
    return this.documentType !== DocumentTypeEnum.XbrlInline && this.documentType !== DocumentTypeEnum.XbrlZip && this.documentType !== DocumentTypeEnum.GenericTextFile && this.documentType !== DocumentTypeEnum.WageTaxXml;
  }

  // inputs
  @Input() documentId!: string;
  @Input() request!: StatusRequestModel;
  @Input() documentType!: string;
  @Input() goToError: boolean = false;

  translations: {
    columnTime: string;
    columnStatusCode: string;
    columnReference: string;
    columnDescription: string;
  } = <any>{};

  // WageTaxXml is rendered as a self-contained, scoped HTML fragment (see getRenderedHtml() on the
  // backend), so it is injected directly via innerHTML instead of into a Shadow DOM.
  renderedWageTaxHtml: SafeHtml = '';
  get isWageTaxHtmlDocument(): boolean {
    return this.documentType === DocumentTypeEnum.WageTaxXml;
  }

  constructor(private activeModal: NgbActiveModal,
    private documentService: DocumentApiClient,
    private translateService: TranslateService,
    private ref: ChangeDetectorRef,
    private sanitizer: DomSanitizer) {
    this.getTranslations();
  }

  getTranslations() {
    this.translateService.get([
      'Request.Rendered.ColumnTime',
      'Request.Rendered.ColumnStatusCode',
      'Request.Rendered.ColumnReference',
      'Request.Rendered.ColumnDescription'
    ]).subscribe(translation => {
      this.translations.columnTime = translation['Request.Rendered.ColumnTime'];
      this.translations.columnStatusCode = translation['Request.Rendered.ColumnStatusCode'];
      this.translations.columnReference = translation['Request.Rendered.ColumnReference'];
      this.translations.columnDescription = translation['Request.Rendered.ColumnDescription'];
    });
  }

  innerHtml!: string;
  currentPageNumber: number = 1;
  pageWidth: number = 0;
  pageHeight: number = 0;
  isMobile: any;
  setPlaceholder: boolean = false;

  pageElement!: HTMLElement;
  panZoomModelData!: PanZoomModel;
  panleft: number = 0;
  pantop: number = 0;
  scrollHeight!: any;

  xbrlTemplate = 'assets/temp_html_pages/xbrl.html';
  xbrlTemp!: string;
  private documentRendered = false;

  async ngOnInit() {
    await this.renderAsHtml();
  }

  close() {
    this.activeModal.close();
  }

  //#region -- Private methods --
  private async renderAsHtml() {
    if (this.documentRendered) return;
    await this.documentService.getRenderedHtml(this.request.id, this.documentId)
      .then((htmlObservable: Observable<string>) => {
        htmlObservable.subscribe({
          error: (error: HttpErrorResponse) => console.error(error),
          next: (html: string) => {
            if (this.documentType === DocumentTypeEnum.WageTaxXml) {
              // Backend already returns a self-isolating fragment (scoped selectors + `all: revert`), so
              // no Batavia/pdf2htmlEX patches are applied here.
            } else if (this.documentType === DocumentTypeEnum.GenericTextFile || this.documentType === DocumentTypeEnum.LogFile) {
              // This is for specific Digipoort Log files (.Html): we translate the headers, see below:
              html = html
                .replace('<font face="verdana">', "<style>table#LogDigipoort {width:100%; font-family: Lato, Arial;}\ntable#LogDigipoort > td {vertical-align: top; padding: 5px;}\ntable#LogDigipoort > tbody tr:first-child td{font-weight: bold;}\ntable#LogDigipoort > td:nth-child(1){width: 180px;}\ntable#LogDigipoort > td:nth-child(2){width: 100px;}\ntable#LogDigipoort > td:nth-child(3){width: 220px;}\ntable#LogDigipoort > tr:nth-child(even) {background: #f5f5f5;}\ntable#LogDigipoort > tr:nth-child(odd) {background: #fff;}</style>")
                .replace("Tijdstip", this.translations.columnTime)
                .replace("Statuscode", this.translations.columnStatusCode)
                .replace("Kenmerk", this.translations.columnReference)
                .replace("Omschrijving", this.translations.columnDescription)
                .replace("<table>", "<table id='LogDigipoort'>")
                .replace('body {', ':host {')
                .replace(/<div[^>]*style=["'][^"']*display\s*:\s*none;?[^"']*["'][^>]*>.*?<\/div>/gis, '')
                .replace(/<div class="page">/g, '<div class="page" style="margin:auto">')
                .replace(/<div class="header">/g, '<div class="header" style="position: relative;">')
                .replace(/<div class="kvk-tmp-page">/g, '<div class="kvk-tmp-page" style="margin:auto">')
                .replace(/justify-content: center;/g, 'justify-content: normal !important;')
                .replace('color: #aa418c;', 'color: #aa418c !important;');
            } else if (this.documentType === DocumentTypeEnum.XbrlInline || this.documentType === DocumentTypeEnum.XbrlZip) {

              let policy = getTrustedXbrliPolicy();
              const parser = new DOMParser();
              const doc = parser.parseFromString(policy!.createHTML(html), 'text/html');

              doc.querySelectorAll('.page .header').forEach(headerClass => headerClass.remove());
              doc.querySelectorAll('.hidden').forEach(hiddenClass => hiddenClass.remove());
              doc.querySelectorAll('.page').forEach(page => {
                (page as HTMLElement).style.marginLeft = 'auto';
                (page as HTMLElement).style.marginRight = 'auto';
              });

              html = doc.documentElement.outerHTML;

              html = this.removeInlineXbrlHtmlHeader(html);
              html = html.replace(/#e07d48/g, "#0087FF")
                .replace(/#f58020/g, "#0087FF")
                .replace(/bisque/g, "#e0f1ff")
                .replace(/#3367e0/g, "#0087FF")
                .replace("</head>", "<style>table{width:100%;}\ntd {vertical-align: top;}\ntbody tr:first-child td{font-weight: bold !important;}\n.sbr-logo, .entrypoint, .logo, .menu-remaining{display:none}\n.display-none, menu-info.display-none{display:none}\n</style></head>")
                .replace('<font face="verdana">', "<style>table, #LogDigipoort table{width:100%; font-family: Lato, Arial;}\ntd {vertical-align: top;}\ntbody tr:first-child td{font-weight: bold;}\ntd:nth-child(1){width: 180px;}\ntd:nth-child(2){width: 100px;}\ntd:nth-child(3){width: 220px;}</style>")
                .replace('id="page-container"', 'id="gone-page-container"')
                .replace('</font>', "")
                .replace('id="menu-info"', 'id="menu-info" class="display-none"')
                //Batavia
                .replace('width: 21cm;', "")
                .replace('min-height: 29.7cm;', "")
                .replace('padding: 2cm 2cm 2cm 2cm;', "padding: 20px; margin-bottom: 10px;")
                .replace('box-shadow: 0 0 0.5cm rgba(0,0,0,0.5);', "box-shadow: 2px 3px 5px 0px rgba(0,0,0,0.16);\nborder-radius: 3px;")
                .replace('.linebreak {  border:0; margin:0; height:10pt;  background:white; }', ".linebreak {  border:0; margin:0; }")
                .replace('pre { white-space:pre-wrap;   font-family: sans-serif, Arial; font-size:8pt; }', "pre { white-space:pre-wrap !important;   font-family: sans-serif, Arial !important; font-size:8pt !important; overflow: hidden !important; margin-bottom: 0 !important; }")
                .replace('box-shadow: 0 5px 5px -3px rgba(0, 0, 0, 0.2), 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 3px 14px 2px rgba(0, 0, 0, 0.12);', "box-shadow: 2px 3px 5px 0px rgba(0,0,0,0.16);")
                .replace('<table xmlns="http://www.w3.org/1999/xhtml">', '<table style="width:100%; table-layout:fixed; overflow-x:clip" xmlns="http://www.w3.org/1999/xhtml">')
                .replace('<table>', '<table style="width:100%; table-layout:fixed; overflow-x:clip">')
                .replace('<table>', '<table style="width:100%; table-layout:fixed; overflow-x:clip">')

                .replace('body {', ':host {')
                .replace('background-color: #fff;', '')
                .replace('font-size: 14.5px;', '')
                .replace("margin: 1cm;", "margin: 1cm 0cm;");

              let re = /td colspan="7"/g;
              html = html.replace(re, 'td colspan="8"')


            } else {
              // This is for XBRLs, to present them in a more readable way
              html = html.replace(/#e07d48/g, "#0087FF")
                .replace(/#f58020/g, "#0087FF")
                .replace(/bisque/g, "#e0f1ff")
                .replace(/#3367e0/g, "#0087FF")
                .replace("</head>", "<style>table#xbrlFile {width:100%;}\ntable#xbrlFile > td {vertical-align: top;}\ntable#xbrlFile > tbody tr:first-child td{font-weight: bold !important;}\n.sbr-logo, .entrypoint, .logo, .menu-remaining{display:none}\n.display-none, menu-info.display-none{display:none}\n</style></head>")
                .replace('<font face="verdana">', "<style>table#xbrlFile {width:100%; font-family: Lato, Arial;}\ntable#xbrlFile > td {vertical-align: top;}\ntable#xbrlFile > tbody tr:first-child td{font-weight: bold;}\ntable#xbrlFile > td:nth-child(1){width: 180px;}\ntable#xbrlFile > td:nth-child(2){width: 100px;}\ntable#xbrlFile > td:nth-child(3){width: 220px;}</style>")
                .replace('</font>', "")
                .replace('id="menu-info"', 'id="menu-info" class="display-none"')
                //Batavia
                .replace('width: 21cm;', "")
                .replace('min-height: 29.7cm;', "")
                .replace('padding: 2cm 2cm 2cm 2cm;', "padding: 20px; margin-bottom: 10px;")
                .replace('box-shadow: 0 0 0.5cm rgba(0,0,0,0.5);', "box-shadow: 2px 3px 5px 0px rgba(0,0,0,0.16);\nborder-radius: 3px;")
                .replace('.linebreak {  border:0; margin:0; height:10pt;  background:white; }', ".linebreak {  border:0; margin:0; }")
                .replace('pre { white-space:pre-wrap;   font-family: sans-serif, Arial; font-size:8pt; }', "pre { white-space:pre-wrap !important;   font-family: sans-serif, Arial !important; font-size:8pt !important; overflow: hidden !important; margin-bottom: 0 !important; }")
                .replace('box-shadow: 0 5px 5px -3px rgba(0, 0, 0, 0.2), 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 3px 14px 2px rgba(0, 0, 0, 0.12);', "box-shadow: none;")
                .replace('<table xmlns="http://www.w3.org/1999/xhtml">', '<table style="width:100%; table-layout:fixed; overflow-x:clip" xmlns="http://www.w3.org/1999/xhtml" id="xbrlFile">')
                .replace('<table>', '<table style="width:100%; table-layout:fixed; overflow-x:clip" id="xbrlFile">')
                .replace('<td colspan="3">', '<td colspan="7">')
                .replace('body {', ':host {')
            }

            this.documentRendered = true;
            if (this.isWageTaxHtmlDocument) {
              this.renderedWageTaxHtml = this.sanitizer.bypassSecurityTrustHtml(html);
            } else {
              renderDocumentIntoShadowRoot('renderDocumentElement' + this.documentId, html);
            }

            this.ref.detectChanges()
            // fixHtml() and scrollToError() operate on host.shadowRoot, which WageTaxXml does not use.
            if (this.isWageTaxHtmlDocument) return;
            this.fixHtml();
            if (this.goToError) {
              this.scrollToError();
            }
          }
        });
      });

  }

  private fixHtml() {
    let host: HTMLElement = document.getElementById('renderDocumentElement' + this.documentId)!;
    let temp: ShadowRoot = host.shadowRoot!;
    Array.from(temp.querySelectorAll('a')).forEach(function (ele) {
      ele.href = "javascript:void(0)";
      ele.target = "";
    });
    let count = 1;
    const pageholders = Array.from(temp.querySelectorAll('.pageholder'));
    pageholders.forEach(function (ele, idx) {
      if (idx === pageholders.length - 1) {
        ele.id = 'error-pageholder';
      } else {
        ele.id = count + "_indicator";
        count++;
      }
    });
  }

  // Scroll to error section
  private scrollToError(): void {
    const host = document.getElementById('renderDocumentElement' + this.documentId);
    const activeAction = host?.shadowRoot?.getElementById('error-pageholder');
    if (activeAction) {
      activeAction.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private removeInlineXbrlHtmlHeader(html: string): string {
    let temp = document.createElement('div');
    let policy = getTrustedXbrliPolicy();
    temp.innerHTML = policy!.createHTML(html);

    temp.querySelectorAll('.header').forEach(el => el.remove());

    return temp.innerHTML;
  }

  //#endregion
}
